import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

type Theme = 'light' | 'dark' | 'system'

interface ThemeContextType {
  theme: Theme
  resolved: 'light' | 'dark'
  setTheme: (theme: Theme) => void
  /** Convenience toggle: cycles light ↔ dark (skips "system"). */
  toggleTheme: () => void
}

export interface ThemeProviderProps {
  children: ReactNode
  /**
   * localStorage key used to persist the theme choice.
   * @default 'checker-theme'
   */
  storageKey?: string
  /**
   * How the resolved theme is applied to `document.documentElement`.
   * - `'class'` (default): adds/removes `'light'`/`'dark'` CSS classes.
   * - `'data-theme'`: sets the `data-theme` attribute.
   */
  attribute?: 'class' | 'data-theme'
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

function getResolved(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return theme
}

function applyTheme(resolved: 'light' | 'dark', attribute: 'class' | 'data-theme') {
  if (attribute === 'data-theme') {
    document.documentElement.setAttribute('data-theme', resolved)
  } else {
    document.documentElement.classList.remove('light', 'dark')
    document.documentElement.classList.add(resolved)
  }
}

export function ThemeProvider({
  children,
  storageKey = 'checker-theme',
  attribute = 'class',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(storageKey) as Theme | null
    return stored || 'system'
  })
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => getResolved(theme))

  useEffect(() => {
    const r = getResolved(theme)
    setResolved(r)
    applyTheme(r, attribute)
  }, [theme, attribute])

  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      const r = getResolved('system')
      setResolved(r)
      applyTheme(r, attribute)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme, attribute])

  const setTheme = (t: Theme) => {
    setThemeState(t)
    localStorage.setItem(storageKey, t)
  }

  const toggleTheme = () => {
    setTheme(resolved === 'dark' ? 'light' : 'dark')
  }

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
